// src/shell/SuiteShellAdapter.tsx
// CRM's adapter for @levelup/ui-shell
//
// This file maps CRM's selectors, hooks, and menu configuration to the shared shell.
// ARCHITECTURE: All fields are MODULE-LEVEL CONSTANTS for stable references.

import {
  SuiteShellProvider,
  SuiteShell,
  type SuiteShellAdapter,
} from '@levelup/ui-shell';
import {
  selectBootApps,
  selectBootMeta,
  selectBootCurrentWorkspace,
  selectBootUser,
  selectBootOtherWorkspaces,
  selectCreateWorkspaceUrl,
} from '../store/slices/bootContextSlice';
import { selectTokenWorkspaceId } from '../store/slices/centralAuthSlice';
import { beginRpWorkspaceTransition } from '../auth/rpWorkspaceTransition';
import { requestCanonicalWorkspaceSwitch } from '../auth/canonicalWorkspaceSwitch';
import { requestCanonicalAppLaunch } from '../auth/canonicalAppLaunch';
import { currentReturnTo } from '../auth/currentReturnTo';
import { useAuth } from '../hooks/useAuth';
import { useUnreadNotificationCount } from '../hooks/useUnreadNotificationCount';
import { useTestingModeState } from '../hooks/useTestingModeState';
import { useGetMenuQuery } from './useStaticShellMenu';
import { getMenuIcon } from '../components/layout/menuIcons';
import { NotificationPanelContent } from '../components/notifications/NotificationPanelContent';
import { TestingModePanelContent } from '../components/testingMode/TestingModePanelContent';
import { AttendanceHeaderQuickAction } from '../components/attendance/AttendanceHeaderQuickAction';
import { SignOutQuickAction } from '../components/auth/SignOutQuickAction';
import {
  APP_ID,
  AUTH_MODE,
  HANDOFF_OIDC_CRM_ENABLED,
  SUITE_HOME_FALLBACK_URL,
} from '../config/centralAuth';
import type { MenuKey } from '../types/api';
import type { RootState } from '../store/store';

// ============================================================
// MENU ROUTES - MODULE-LEVEL CONSTANT
// ============================================================

const MENU_ROUTES: Record<MenuKey, string> = {
  home: '/',
  contacts: '/contacts',
  // Companies is a TAB of Contacts now; the key is retained so this table stays
  // total over MenuKey, and points at the canonical tab path so it can never
  // resurrect the removed top-level destination.
  companies: '/contacts/companies',
  tasks: '/tasks',
  pipeline: '/pipeline',
  // M1 — same correction as SideMenu/BottomNav. This map drives the suite
  // shell's own navigation, so leaving it stale would reintroduce the defect
  // for anyone running CRM inside the shell.
  messages: '/messages',
  // Retained for the same reason: `emails` is no longer emitted by the menu,
  // and email is a medium filter inside Messages.
  emails: '/messages?medium=email',
  phone: '/phone',
  sms: '/sms',
  schedule: '/schedule',
  circles: '/circles',
  referrals: '/referrals',
  team: '/team',
  forms: '/forms',
  files: '/files',
  projects: '/projects',
  offerings: '/offerings',
  payments: '/payments',
  businessProfile: '/business-profile',
  portfolio: '/portfolio',
  recruitment: '/recruitment',
  marketplace: '/marketplace',
  channels: '/channels',
  settings: '/settings',
};

/**
 * The route table, exported for tests only.
 *
 * The rail's active state is decided by React Router matching these routes
 * against the URL, so "which entries light up on this URL" is answerable only
 * with the real table. `navigationActiveState.test.tsx` uses it to pin the one
 * rule that matters: never two entries at once.
 */
export const MENU_ROUTES_FOR_TEST = MENU_ROUTES;

// ============================================================
// RP/BFF NORMALIZATION — SHELL-BOUNDARY ADAPTERS
// ============================================================
//
// H-2C2 — same predicate-drift class as PR #23, PR #24, PR #25, applied
// at the SuiteShell adapter boundary. The shared `@levelup/ui-shell`
// (v1.0.1) `LeftNavigation` reads:
//
//   const { authSource } = hooks.useAuth();
//   const tokenWorkspaceId = useShellSelector(selectors.tokenWorkspaceId);
//   const activeWorkspaceId = authSource === "central" ? tokenWorkspaceId : void 0;
//   const { data: menuData } = hooks.useGetMenuQuery(activeWorkspaceId || "", {
//     skip: !activeWorkspaceId
//   });
//
// For RP/BFF users:
//   - `useAuth().authSource` is null (or 'legacy') because Central auth is
//     server-side via `crm_sid`; no browser-side token populates Redux.
//   - `selectTokenWorkspaceId` parses the JWT workspaceId from
//     `centralAuth.accessToken` — also null for RP.
//
// Both pre-fix values cause `activeWorkspaceId` to be `undefined` →
// `useGetMenuQuery` is skipped → `menuData` is `undefined` → the left-nav
// renders ZERO items. The shell mounts (PR #25 fixed that) but its menu
// is empty.
//
// We CANNOT modify `@levelup/ui-shell` from here (versioned package).
// Instead we normalize the inputs at the adapter boundary — exactly the
// pattern PR #24 used for `isShellEnabled`:
//
//   1. `useAuthForShell` wraps `useAuth` and overrides `authSource` to
//      `'central'` when `AUTH_MODE === 'central' || authSource === 'central'`.
//      Other consumers of CRM's `useAuth` are unaffected (they continue
//      to import from `hooks/useAuth` directly).
//
//   2. `selectShellTokenWorkspaceId` falls back to
//      `bootContext.currentWorkspace.id` when the JWT-derived value is
//      null. For RP users, `bootContext.currentWorkspace.id` is the
//      authoritative active workspace identifier (set server-side and
//      returned via /auth/context). Other consumers of
//      `selectTokenWorkspaceId` are unaffected.
//
// Both wrappers live in this file only and are passed in via the adapter
// — keeping the shell-boundary normalization isolated and the rest of
// CRM auth contracts unchanged.

function useAuthForShell(): ReturnType<typeof useAuth> {
  const auth = useAuth();
  if (AUTH_MODE === 'central' || auth.authSource === 'central') {
    return { ...auth, authSource: 'central' };
  }
  return auth;
}

function selectShellTokenWorkspaceId(state: RootState): string | null {
  const fromToken = selectTokenWorkspaceId(state);
  if (fromToken) return fromToken;
  // RP fallback: bootContext.currentWorkspace.id is the authoritative
  // active workspace for RP users (no browser-side JWT to parse).
  return selectBootCurrentWorkspace(state)?.id ?? null;
}

// ============================================================
// RP/BFF WORKSPACE-SWITCH HOOK (ui-shell 1.2.4+ `onWorkspaceSwitch`)
// ============================================================
//
// In Bearer mode the shell's HeaderBar calls `useAuth().switchWorkspace`,
// which does a client-side `selectWorkspaceViaBearer(centralToken, ...)`.
// In RP/BFF mode (`HANDOFF_OIDC_CRM_ENABLED=true`) there is no browser-side
// Bearer token — `useAuth().switchWorkspace` throws
//   "Workspace switching requires Central authentication"
// at frontend/src/hooks/useAuth.ts:242, which the shell catches and logs as
//   "[HeaderBar] Failed to switch workspace: ..."
//
// ui-shell 1.2.4 introduced `config.onWorkspaceSwitch?: (workspaceId) =>
// Promise<void>` precisely for this case: when present, HeaderBar calls
// this INSTEAD of `useAuth().switchWorkspace`. We use it to perform a full
// `/auth/start` re-auth round-trip, which:
//   1. carries `workspaceId` to PR #64's mismatch-clearing logic, which
//      destroys the stale `crm_sid` session if its bound workspace differs,
//   2. proceeds to Central `/authorize` → `/auth/callback`, minting a new
//      `crm_sid` for the intended workspace. PR #69 forwards `workspaceId`
//      to `/api/oidc/authorize` so Central PR #194 binds the auth code to
//      the requested workspace instead of the user's default. Together:
//      the new `crm_sid` is correctly bound to the intended workspace.
//   3. If the new workspace doesn't entitle CRM (e.g. personal workspaces),
//      ui-shell 1.2.5's `useAppAccessGuard` detects `crm` absent from
//      apps[] and redirects via `home.launchUrl ?? home.webUrl`, preserving
//      the workspace context across the cross-app handoff.
//
// We register this hook ONLY when the RP handoff is on, so Bearer-mode
// users continue to see the existing `useAuth().switchWorkspace` path.
// Returning `undefined` makes ui-shell fall back to the default.
export const rpWorkspaceSwitchHandler: ((workspaceId: string) => Promise<void>) | undefined =
  HANDOFF_OIDC_CRM_ENABLED
    ? async (workspaceId: string): Promise<void> => {
        // Browser navigation is a hard handoff; the shell awaits this promise
        // before closing the switcher panel, but the transition tears down the
        // page. Delegates to the shared `beginRpWorkspaceTransition` primitive
        // (same URL/behaviour as before) so the Workspaces page can reuse the
        // exact same RP flow without duplicating it. Malformed input is NOT
        // rejected here — PR #64's server-side UUID validator is the guard.
        //
        // Preserve the user's CURRENT safe local route as returnTo: the shell
        // workspace switcher fires from ANY route, and without this the
        // successful re-mint returned everyone to `/` (the primitive's
        // defensive default). The server `validateReturnTo` re-checks it.
        beginRpWorkspaceTransition(workspaceId, currentReturnTo());
        // The page is now navigating; resolve so the shell closes the panel.
      }
    : undefined;

// ============================================================
// ADAPTER - MODULE-LEVEL CONSTANT
// ============================================================

/**
 * CRM's adapter for the shared Suite Shell.
 *
 * CRITICAL: This must be a module-level constant.
 * Do NOT create this object inside a component or hook.
 */
const ADAPTER: SuiteShellAdapter<RootState> = {
  selectors: {
    apps: selectBootApps,
    meta: selectBootMeta,
    currentWorkspace: selectBootCurrentWorkspace,
    user: selectBootUser,
    otherWorkspaces: selectBootOtherWorkspaces,
    // RP-aware: falls back to bootContext.currentWorkspace.id when no
    // browser-side JWT exists. See block-comment above.
    tokenWorkspaceId: selectShellTokenWorkspaceId,
    createWorkspaceUrl: selectCreateWorkspaceUrl,
  },
  hooks: {
    // RP-aware: returns `authSource: 'central'` when AUTH_MODE is central
    // OR the user is centrally authenticated. See block-comment above.
    useAuth: useAuthForShell,
    useGetMenuQuery,
    useUnreadCount: useUnreadNotificationCount,
    useTestingModeState,
  },
  config: {
    appId: APP_ID,
    offloadRoute: '/offload-responsibilities',
    settingsRoute: '/settings',
    // ui-shell 1.2.4+ `onWorkspaceSwitch` hook. Defined ONLY in RP/BFF mode
    // (see block-comment + `rpWorkspaceSwitchHandler` above). When
    // `undefined`, the shell falls back to `hooks.useAuth().switchWorkspace`
    // — preserving Bearer-mode behavior verbatim.
    // ui-shell 1.14.0 CANONICAL workspace switch. Takes precedence over
    // `onWorkspaceSwitch` below, which is now legacy: it sent the user to
    // CRM's OWN /auth/start for a workspace CRM may not be admitted to, and
    // then relied on the access guard to bounce them onward using a raw
    // registry URL. Central now resolves the destination app and moves the
    // session atomically, so neither step is CRM's to make.
    //
    // Registered unconditionally — unlike the RP handler below, this does
    // not depend on the handoff flag. The endpoint authenticates with
    // Central's session cookie, which a Bearer-mode browser holds too.
    requestWorkspaceSwitch: requestCanonicalWorkspaceSwitch,
    // ui-shell 1.15.0 CANONICAL app launch. Replaces navigating to
    // `app.launchUrl ?? app.webUrl` out of the boot snapshot — a DISPLAY url
    // carrying no authentication, read from a list that may be stale.
    // Central validates admission against the CURRENT workspace and supplies
    // the app's own entry point, which may be path-prefixed (BrandTalk's is
    // `/brand/auth/start`), so the shell never constructs it.
    requestAppLaunch: requestCanonicalAppLaunch,
    onWorkspaceSwitch: rpWorkspaceSwitchHandler,
    // ui-shell 1.2.5 `useAppAccessGuard` `fallbackUrl`. Consumed ONLY when
    // the guard enters the "currentAppId absent from apps[], no user-facing
    // app available" branch — a degenerate state that should never fire when
    // /auth/context returns sensible apps[] (it always includes `home` for
    // personal workspaces and the entitled set for business workspaces).
    // Belt-and-suspenders: prevents the guard from returning `decision:
    // 'block'`, which would render the redirect placeholder with no target.
    // See `SUITE_HOME_FALLBACK_URL` for the resolution order.
    fallbackUrl: SUITE_HOME_FALLBACK_URL,
  },
  adapters: {
    menuRoutes: MENU_ROUTES,
    getMenuIcon: (menuKey: string, className?: string) =>
      getMenuIcon(menuKey as MenuKey, className),
    NotificationPanel: NotificationPanelContent,
    TestingModePanel: TestingModePanelContent,
    // ui-shell 1.4.0 header quick-action slot. CRM-owned Attendance control,
    // rendered by the shell in the header action row (shell owns placement/
    // spacing/a11y container; this component owns its state/menu/actions).
    HeaderQuickActions: [AttendanceHeaderQuickAction, SignOutQuickAction],
  },
};

// ============================================================
// COMPONENT
// ============================================================

/**
 * CRM's SuiteShell integration.
 * Wraps the shared SuiteShell with CRM's adapter.
 *
 * Usage:
 * ```tsx
 * if (isShellEnabled({ authSource, meta, forceUiShell })) {
 *   return <CRMSuiteShell />;
 * }
 * ```
 */
export function CRMSuiteShell() {
  // ui-shell 1.13.0 render-time recovery props. Both exist because a member
  // refused this app gets a full-screen explanation INSTEAD of the shell —
  // no header, no navigation, no workspace switcher — and before 1.13.0 that
  // screen offered nothing at all. A member who simply switched into a
  // workspace that does not carry CRM was stranded there.
  //
  // WHY PROPS RATHER THAN `ADAPTER.config`: `ADAPTER` is module-level by
  // design, so it cannot hold a hook-owned value. `logout` composes CRM's
  // Central and legacy sign-out and lives in `useAuth`; reaching it from
  // static config would have meant duplicating session-clearing logic. The
  // render-time props exist precisely so hosts do not pay that cost.
  const { logout } = useAuth();

  return (
    <SuiteShellProvider adapter={ADAPTER}>
      <SuiteShell
        // Deliberately explicit rather than letting ui-shell derive Home from
        // `apps[]`. Derivation is the right shared default, but Home's
        // presence there is workspace-dependent: a personal workspace returns
        // it, and a business workspace legitimately may not (measured on live
        // Central staging — 11 apps, no `home`). Deriving alone would leave a
        // member-not-assigned user in a BUSINESS workspace with no way out.
        //
        // `SUITE_HOME_FALLBACK_URL` is CRM's canonical Suite Home destination
        // (VITE_HOME_URL, else the crm→home host rewrite), so this invents no
        // URL and adds no configuration. It is passed through the `homeUrl`
        // API as a user-initiated destination — ui-shell's own `fallbackUrl`
        // is NOT a Home source and nothing here revives it.
        homeUrl={SUITE_HOME_FALLBACK_URL}
        onSignOut={logout}
      />
    </SuiteShellProvider>
  );
}

export default CRMSuiteShell;
